import React, { useEffect, useRef, useState } from "react";
import { Search, Menu } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { cn } from "@weco/ui";
import type { SuiteChromeMode } from "./SuiteSidebar";
import type { SuiteChromeUser } from "./types";
import { UserMenu, type UserMenuLabels } from "./UserAvatar";
import { GlobalSearchDropdown } from "./global-search/GlobalSearchDropdown";
import { useGlobalSearch } from "./global-search/useGlobalSearch";
import { NotificationsBell } from "./notifications/NotificationsBell";
import type { NotificationsDropdownLabels } from "./notifications/NotificationsDropdown";

export interface TopbarLabels extends UserMenuLabels {
  searchAriaLabel?: string;
  notificationsLabel?: string;
  notificationsEmptyLabel?: string;
  /** The rest of the notifications panel's strings (mark all read, states). */
  notifications?: NotificationsDropdownLabels;
  mobileSearchAriaLabel?: string;
  openMenuLabel?: string;
  closeMenuLabel?: string;
}

export interface SuiteTopbarProps {
  mode: SuiteChromeMode;
  currentAppId?: string;
  user?: SuiteChromeUser | null;
  onShellSignOut?: () => void | Promise<void>;
  onLogoClick?: () => void;
  brandingAppName?: string;
  brandingLogoUrl?: string;
  searchPlaceholder?: string;
  topbarLabels?: TopbarLabels;
  /** Mobile: opens the apps sheet. Only rendered in the condensed layout. */
  onMenuToggle?: () => void;
  isMenuOpen?: boolean;
  /** Mobile (<768): condensed header — menu · logo · status · avatar. */
  condensed?: boolean;
  /** The tab rail (desktop/tablet). */
  tabs?: React.ReactNode;
  /** Work status (HomeWork presence + timer), or null when unavailable. */
  status?: React.ReactNode;
  /** Avatar menu "View profile". */
  onProfileClick?: () => void;
}

/**
 * The header (design: `weco2/shell.jsx` Header). It is the gray underlay of
 * the workspace surface: the active tab flares straight into the content
 * panel below. Desktop/tablet: tabs · separator · work status · glass cluster
 * (collapsible search, notifications, avatar). Mobile: menu · logo · status ·
 * avatar, with search and notifications moved into the apps sheet.
 */
export const SuiteTopbar: React.FC<SuiteTopbarProps> = ({
  user,
  onShellSignOut,
  onLogoClick,
  brandingAppName = "We-Co",
  brandingLogoUrl,
  searchPlaceholder = "Search workspace...",
  topbarLabels,
  onMenuToggle,
  isMenuOpen = false,
  condensed = false,
  tabs,
  status,
  onProfileClick,
}) => {
  const {
    notificationsLabel = "Notifications",
    notificationsEmptyLabel = "You're all caught up.",
    openMenuLabel = "Open menu",
    closeMenuLabel = "Close menu",
  } = topbarLabels ?? {};

  const avatar = (
    <UserMenu
      user={user}
      onSignOut={onShellSignOut}
      onProfile={onProfileClick}
      labels={topbarLabels}
      className={cn(!condensed && "ms-0.5")}
    />
  );

  if (condensed) {
    return (
      <header
        className="wc-hdr-mobile flex gap-2 sticky top-0 z-30 shrink-0 h-14 items-center"
        style={{ margin: "4px 0 0", padding: "0 12px" }}
      >
        <div className="flex items-center gap-1 shrink-0">
          {onMenuToggle && (
            <button
              type="button"
              onClick={onMenuToggle}
              title={isMenuOpen ? closeMenuLabel : openMenuLabel}
              aria-label={isMenuOpen ? closeMenuLabel : openMenuLabel}
              aria-expanded={isMenuOpen}
              aria-controls="suite-mobile-apps"
              className="wc-hdr-btn w-9 h-9 rounded-[14px] flex items-center justify-center text-foreground/70 hover:bg-foreground/5 hover:text-foreground active:scale-95 transition-all shrink-0"
            >
              <Menu size={18} strokeWidth={2} aria-hidden />
            </button>
          )}
          <button
            type="button"
            onClick={onLogoClick}
            title={brandingAppName}
            aria-label={`${brandingAppName} home`}
            className="flex items-center gap-2 min-h-[44px] shrink-0 hover:opacity-80 active:scale-95 transition-all"
          >
            {brandingLogoUrl ? (
              <img src={brandingLogoUrl} alt="" className="w-8 h-8 shrink-0 rounded-xl object-cover" />
            ) : (
              <span className="w-8 h-8 shrink-0 bg-primary rounded-xl flex items-center justify-center">
                <span className="font-black text-base text-primary-foreground lowercase leading-none">w</span>
              </span>
            )}
            <span data-i18n-skip="true" className="font-black text-sm tracking-tight text-foreground whitespace-nowrap">
              {brandingAppName}
            </span>
          </button>
        </div>
        <div className="flex-1" />
        <div className="flex items-center gap-2 shrink-0 self-center">
          {status}
          {avatar}
        </div>
      </header>
    );
  }

  return (
    <header
      className="flex gap-2 sticky top-0 z-30 shrink-0 h-11 items-end"
      style={{ margin: "4px 0 0", padding: "0 12px" }}
    >
      {tabs ?? <div className="flex-1" />}
      {tabs && <div aria-hidden="true" className="shrink-0 self-center bg-border" style={{ width: 1, height: 24 }} />}
      <div className="flex items-center gap-2 shrink-0 self-center" style={{ paddingInlineStart: 2 }}>
        {status}
        <div className="glass-bar flex items-center gap-0.5 rounded-2xl p-0.5" style={{ height: 35 }}>
          <HeaderSearch placeholder={searchPlaceholder} labels={topbarLabels} />
          <NotificationsBell
            label={notificationsLabel}
            labels={{ empty: notificationsEmptyLabel, ...topbarLabels?.notifications }}
          />
          {avatar}
        </div>
      </div>
    </header>
  );
};

/** Collapsible header search (design: HeaderSearch) wired to global search. */
function HeaderSearch({ placeholder, labels }: { placeholder: string; labels?: TopbarLabels }) {
  const { searchAriaLabel = "Search workspace" } = labels ?? {};
  const [q, setQ] = useState("");
  const [focused, setFocused] = useState(false);
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { modules } = useGlobalSearch(q);
  const expanded = focused || q.length > 0;

  useEffect(() => {
    setOpen(false);
    setFocused(false);
  }, [location.pathname]);

  useEffect(() => {
    setOpen(q.length >= 2);
  }, [q]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key.toLowerCase() === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div
      ref={wrapperRef}
      className={cn(
        "relative flex items-center h-9 rounded-full overflow-hidden transition-[width,background-color] duration-300 ease-out",
        expanded ? "w-40 sm:w-52 bg-foreground/5" : "w-9",
      )}
    >
      <span className="absolute start-0 w-9 h-9 flex items-center justify-center text-foreground/60 pointer-events-none">
        <Search size={18} aria-hidden />
      </span>
      {!expanded && (
        <button
          type="button"
          onClick={() => {
            setFocused(true);
            requestAnimationFrame(() => inputRef.current?.focus());
          }}
          title={searchAriaLabel}
          aria-label={searchAriaLabel}
          className="absolute inset-0 rounded-full text-foreground/70 hover:bg-foreground/5 transition-colors"
        />
      )}
      <input
        ref={inputRef}
        type="text"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => {
          setFocused(true);
          if (q.length >= 2) setOpen(true);
        }}
        onBlur={() => setFocused(false)}
        onKeyDown={(e) => {
          if (e.key === "Escape") (e.target as HTMLInputElement).blur();
        }}
        placeholder={placeholder}
        aria-label={searchAriaLabel}
        className={cn(
          "h-9 w-full bg-transparent outline-none text-sm font-medium ps-9 pe-3 placeholder:text-muted-foreground/60 transition-opacity duration-200",
          expanded ? "opacity-100" : "opacity-0 pointer-events-none",
        )}
      />
      <GlobalSearchDropdown
        open={open}
        modules={modules}
        anchorRef={wrapperRef}
        onSelect={(r) => {
          navigate(r.href);
          setOpen(false);
        }}
        onClose={() => {
          setOpen(false);
          inputRef.current?.blur();
        }}
      />
    </div>
  );
}
